import { KeyboardEvent, useRef } from 'react';
import { useTranslations } from 'next-intl';
import paths from '@constants/paths';
import KEYS from '@helpers/keys';
import Text from '@atoms/Text/Text';
import Config from '@config';
import {
  SearchParamsWrapper,
  StyledListbox,
  StyledContainer,
  StyledEmptySlotContainer,
  StyledInfoBox,
  StyledSpinner,
  StyledEmptySearchContainer,
} from './PickUpInStoreSearch.styles';
import Input from '@atoms/Input/Input';
import useCart from '@hooks/useCart';
import useFinishPickUpSubmission from './useFinishPickUpSubmission';
import Link from 'next/link';
import ScrollShadow from '@components/molecules/ScrollShadow/ScrollShadow';
import PickupLocationListItem, { type SuggestionType } from '@molecules/PickupLocationListItem/PickupLocationListItem';
import { usePickupLocationSearch } from './usePickUpLocationSearch';
import { trackOnlineStoreChanged } from '@helpers/analyticsHelpers/trackStore';
import useListboxNavigation from '@hooks/useListboxNavigation';
import type { AxfoodStoreInfoViewModel } from '@occ/api-client';

export interface PickupInStoreSearchProps {
  afterSubmit: () => void;
}

interface EmptySearchStateProps {
  searchTerm: string;
}

const EmptySearchState = ({ searchTerm }: EmptySearchStateProps) => {
  const t = useTranslations('deliverypicker');

  return (
    <StyledEmptySearchContainer>
      <StyledInfoBox>
        <Text align="center" type="body" size="small" color="black">
          {t('autoComplete.pickupLocation.emptySlot.head', {
            search: searchTerm,
          })}
        </Text>
      </StyledInfoBox>

      <StyledEmptySlotContainer>
        <Text align="center" type="body" size="small" color="black">
          {t('autoComplete.pickupLocation.emptySlot.text')}
        </Text>
        <Text align="center" type="body" size="small" color="black">
          {t('autoComplete.pickupLocation.emptySlot.notfound')}
        </Text>
      </StyledEmptySlotContainer>

      <Text align="center" type="body" size="small" color="black">
        <Link href={paths.PICKUP_STORES} target="_blank">
          {t('autoComplete.pickupLocation.emptySlot.link')}
        </Link>
      </Text>
    </StyledEmptySearchContainer>
  );
};

const PickupInStoreSearch = ({ afterSubmit }: PickupInStoreSearchProps) => {
  const t = useTranslations('deliverypicker');
  const { cart } = useCart();
  const isOrderReopened = Boolean(cart?.orderReference);

  // Type-safe mapping from delivery mode codes to translation keys
  const deliveryModeInfoKeys = {
    [Config.PICKUP_IN_STORE]: 'info.pickUpInStore',
    [Config.HOME_DELIVERY]: 'info.homeDelivery',
  } as const;

  const autoCompleteRef = useRef<HTMLDivElement>(null);

  const { finishPickUpSubmission, isLoading: isLoadingFinishPickUp } = useFinishPickUpSubmission({
    afterSubmit,
  });

  const {
    inputValue,
    suggestions,
    suggestionForYou,
    isLoading: isLoadingSuggestions,
    setInputValue,
    setSuggestions,
    onInputChange,
  } = usePickupLocationSearch();

  const isLoading = isLoadingSuggestions || isLoadingFinishPickUp;

  const handleSubmit = (suggestion: SuggestionType) => {
    const name = suggestion.displayName ?? '';
    const storeId = suggestion.storeId ?? '';
    if (!name || !storeId) return;
    // Selected store name is set to input field without triggering search
    setInputValue(name);
    trackOnlineStoreChanged(storeId);
    // If cart conflict is aborted, trigger input change to search for the selected store
    finishPickUpSubmission(storeId, () => onInputChange(name));
  };

  const handleClear = () => {
    setInputValue('');
    setSuggestions([]);
  };

  const { getListItemProps } = useListboxNavigation({
    listRef: autoCompleteRef,
    onSelect: (index) => {
      const currentItems = showSuggestionForYou ? [suggestionForYou] : suggestions;
      const item = currentItems[index];
      if (item) {
        // Cast needed: API returns displayName at runtime but AxfoodStoreInfoViewModel doesn't declare it
        handleSubmit(item as unknown as SuggestionType);
      }
    },
  });

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case KEYS.TAB:
        e.currentTarget.blur();
        break;
      case KEYS.UP:
        e.preventDefault();
        break;
      case KEYS.DOWN:
        e.preventDefault();
        autoCompleteRef.current?.querySelector<HTMLElement>('[role="option"]')?.focus();
        break;
      default:
        break;
    }
  };

  const showEmptyState = !isLoading && !suggestions?.length && inputValue.length >= 2;
  const showSuggestionForYou = !isLoading && suggestionForYou && inputValue.length === 0;

  const items = showSuggestionForYou ? [suggestionForYou] : suggestions;

  return (
    <>
      <SearchParamsWrapper>
        <Text type="body" size="medium">
          {t(deliveryModeInfoKeys[Config.PICKUP_IN_STORE])}
        </Text>
        <Input
          name="searchStore"
          type="search"
          showSearchIcon
          aria-label={t('placeholder.store')}
          placeholder={t('placeholder.store')}
          value={inputValue}
          onChange={(e) => onInputChange(e.currentTarget.value)}
          onClear={handleClear}
          onKeyDown={handleKeyDown}
          clearable
          autoFocus
        />
      </SearchParamsWrapper>

      {!isLoading && showSuggestionForYou && (
        <StyledContainer>
          <Text type="label">{t('autoComplete.suggestionForYou')}</Text>
        </StyledContainer>
      )}
      <ScrollShadow>
        {showEmptyState ? (
          <EmptySearchState searchTerm={inputValue} />
        ) : (
          <StyledListbox listRef={autoCompleteRef} items={items} getListItemProps={getListItemProps} cards>
            {(item: AxfoodStoreInfoViewModel) => (
              <PickupLocationListItem
                suggestion={item as unknown as Parameters<typeof PickupLocationListItem>[0]['suggestion']}
                searchTerm={inputValue}
                onItemClick={handleSubmit}
                isOrderReopened={isOrderReopened}
              />
            )}
          </StyledListbox>
        )}
        {isLoading && <StyledSpinner size={32} color="black" />}
      </ScrollShadow>
    </>
  );
};

export default PickupInStoreSearch;
